import React from "react";
import { useTranslation } from "react-i18next";
import { TFunction } from "i18next";
import { Link as RouterLink } from "react-router";
import { format, parseJSON } from "date-fns";
import { Link, Typography } from "@mui/material";
import { Expand, RotateRight } from "@mui/icons-material";
import { TransactionV2 } from "@rentablo/api/apiv1";
import { GainLossTypography } from "@rentablo/components";
import { toCurrency } from "@helpers";
import { MobileListRow } from "@components/MobileList";
import {
  TransactionStatusIcon,
  transactionNeedsAttention,
} from "@features/investments/investment/components/TransactionStatusIcon";

const CENTS = { minimumFractionDigits: 2 };

const nameLinkSx = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
} as const;

const iconSx = { fontSize: 14, flex: "none", color: "icon.secondary" };

export const transactionTitle = (
  transaction: TransactionV2,
  t: TFunction
): string => {
  if (transaction.isAdjustingEntry) {
    return t("singleAccountPage.protocolTable.adjustingEntry");
  }
  return (
    transaction.booking?.investment.name ||
    transaction.counterpartName ||
    transaction.purpose ||
    t("singleAccountPage.protocolTable.noPurpose")
  );
};

interface Props {
  transaction: TransactionV2;
  currency?: string;
  onClick?: (transaction: TransactionV2) => void;
}

export const AccountTransactionMobileRow = ({
  transaction,
  currency = "EUR",
  onClick,
}: Props) => {
  const { t } = useTranslation();
  const investment = transaction.booking?.investment;

  const title = transactionTitle(transaction, t);
  const detail = transaction.isAdjustingEntry
    ? undefined
    : investment || transaction.counterpartName
      ? transaction.purpose
      : transaction.literalType;
  const status = transaction.booking && {
    dataConsistencyStatus: transaction.booking.dataConsistencyStatus,
    dataSource: transaction.booking.dataSource,
    isExternalID: Boolean(transaction.booking.externalId),
  };

  return (
    <MobileListRow
      testId="account-transaction-mobile-row"
      onClick={onClick && (() => onClick(transaction))}
      title={
        <Typography variant="body4" component="div" noWrap minWidth={0}>
          {investment ? (
            <Link
              component={RouterLink}
              to={`/investments/${investment.id}`}
              underline="hover"
              sx={nameLinkSx}
              // The name opens the investment page, not the details.
              onClick={(event: React.MouseEvent) => event.stopPropagation()}
            >
              {investment.name}
            </Link>
          ) : (
            title
          )}
        </Typography>
      }
      titleEnd={
        <GainLossTypography
          value={transaction.amount}
          variant="body4"
          component="span"
          flex="none"
          whiteSpace="nowrap"
        >
          {typeof transaction.amount === "number"
            ? toCurrency(transaction.amount, currency, false, CENTS)
            : "–"}
        </GainLossTypography>
      }
      subtitle={
        <>
          {transaction.isRegular && (
            <RotateRight
              titleAccess={t("cashflowAnalyzer.transactionsTable.regular")}
              sx={iconSx}
            />
          )}
          {transaction.isInterAccountTransfer && (
            <Expand
              titleAccess={t("singleAccountPage.protocolTable.transfer")}
              sx={iconSx}
            />
          )}
          {detail && (
            <Typography
              variant="caption"
              color="textSecondary"
              noWrap
              minWidth={0}
            >
              {detail}
            </Typography>
          )}
          {status && transactionNeedsAttention(status) && (
            <TransactionStatusIcon {...status} size={14} />
          )}
        </>
      }
      subtitleEnd={
        transaction.valueDate && (
          <Typography variant="caption" color="textSecondary" flex="none">
            {format(parseJSON(transaction.valueDate), "dd.MM.yyyy")}
          </Typography>
        )
      }
    />
  );
};
